import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useAuthStore, type AuthUser } from '@/stores/authStore';
import { apiClient } from './api-client';

interface MembershipSummary {
  workspaceId: string;
  workspaceName: string;
  role: string;
}

interface LoginResponseV2 {
  access_token: string;
  refresh_token: string;
  user: {
    id: string;
    email: string;
    displayName: string | null;
    isAdmin: boolean;
  };
  memberships: MembershipSummary[];
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { user, setSession, clear } = useAuthStore();

  const login = useCallback(async (email: string, password: string) => {
    const { data } = await apiClient.post<LoginResponseV2>('/v1/auth/v2/login', { email, password });
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('refresh_token', data.refresh_token);
    const authUser: AuthUser = {
      id: data.user.id,
      email: data.user.email,
      name: data.user.displayName ?? undefined,
      role: data.user.isAdmin ? 'admin' : 'viewer',
    };
    setSession(authUser, data.access_token);
  }, [setSession]);

  const logout = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('current_workspace_id');
    clear();
  }, [clear]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, isAuthenticated: !!user, login, logout }),
    [user, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
